/**
 * Unified data source for the library grid.
 *
 * In normal mode, delegates to useSimpleMediaLibrary (SSE streaming + pagination).
 * In collection mode, delegates to useCollectionItemsQuery (REST + server filtering).
 *
 * Consumers get a single interface regardless of mode:
 *   { items, isLoading, hasMore, loadMore, isLoadingMore, refresh }
 */

import { useMemo } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { useSimpleMediaLibrary } from "@/core/MediaLibrary/hooks/useSimpleMediaLibrary";
import { useCollectionItemsQuery } from "@/core/MediaLibrary/hooks/useCollectionItemsQuery";
import { MediaStage } from "@/core/types/mediaTypes";
import { MediaContentType } from "@/core/types/mediaContentType";
import { selectLibraryItems } from "./libraryItemSelection";

interface LibraryDataSourceOptions {
  clientId: string;
  contentTypes: MediaContentType[];
  stageFilter?: MediaStage[];
  onlyPublic: boolean;
  onlyNonLeadCarousel: boolean;
  websiteTags?: string[];
  /** Collection mode fields */
  collectionId?: string;
  collectionMediaType?: string;
}

interface LibraryDataSource {
  items: MediaItem[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
  refresh: () => void;
  error: string | null;
}

export function useLibraryDataSource(options: LibraryDataSourceOptions): LibraryDataSource {
  const {
    clientId,
    contentTypes,
    stageFilter,
    onlyPublic,
    onlyNonLeadCarousel,
    websiteTags,
    collectionId,
    collectionMediaType,
  } = options;

  const isCollectionMode = !!collectionId;

  // Normal library data source (always called - React hooks can't be conditional)
  const mediaLibrary = useSimpleMediaLibrary({
    clientId,
    autoLoad: true,
    contentTypes,
    stageFilter: isCollectionMode ? undefined : stageFilter,
    onlyNonLeadCarousel,
    websiteTags,
  });

  // Collection data source (enabled only when collectionId is truthy)
  const collectionQuery = useCollectionItemsQuery({
    clientId: isCollectionMode ? clientId : undefined,
    collectionId,
    mediaType: collectionMediaType,
  });

  // Derive items from the appropriate source
  const normalItems = useMemo(() => {
    let items = selectLibraryItems(contentTypes, mediaLibrary);

    if (onlyPublic) {
      items = items.filter((item) => item.publicStatus === "public");
    }

    return items;
  }, [
    contentTypes,
    onlyPublic,
    mediaLibrary.images,
    mediaLibrary.shortVideos,
    mediaLibrary.longVideos,
    mediaLibrary.videos,
    mediaLibrary.postVideos,
    mediaLibrary.audio,
  ]);

  if (isCollectionMode) {
    return {
      items: collectionQuery.items,
      isLoading: collectionQuery.isLoading,
      hasMore: collectionQuery.hasMore,
      loadMore: () => collectionQuery.loadMore(),
      isLoadingMore: collectionQuery.isLoadingMore,
      refresh: () => collectionQuery.refetch(),
      error: collectionQuery.error ? String(collectionQuery.error) : null,
    };
  }

  return {
    items: normalItems,
    isLoading: mediaLibrary.state === "loading",
    hasMore: mediaLibrary.hasMore,
    loadMore: mediaLibrary.loadMore,
    isLoadingMore: mediaLibrary.isLoadingMore,
    refresh: mediaLibrary.refresh,
    error: mediaLibrary.error,
  };
}
